import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Music2, Linkedin, Film, Mail, Sparkles, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicNav from "@/components/PublicNav";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5c54ef11-29b1-4ebe-ae66-9bbd6fa3e33c/images/af713b578a41076e5eea7010c98b40d5c7d59e5336309d74f1c872845aeff8f4.png";
const FEATURE_BG = "https://static.prod-images.emergentagent.com/jobs/5c54ef11-29b1-4ebe-ae66-9bbd6fa3e33c/images/9de431c9bfb6e62cffcb63ea1da101031fbd4774c74cf59141fc4877f489b226.png";

const PLATFORMS = [
    { name: "INSTAGRAM", icon: Instagram },
    { name: "TIKTOK", icon: Music2 },
    { name: "LINKEDIN", icon: Linkedin },
    { name: "SHORTS / REELS", icon: Film },
    { name: "NEWSLETTER", icon: Mail },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-[#050505] text-white">
            <PublicNav />

            {/* HERO */}
            <section className="relative pt-32 pb-24 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30 bg-cover bg-center pointer-events-none"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/40 to-black" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
                    <div className="grid lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8 fade-in-up">
                            <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/15 bg-black/60 backdrop-blur-md rounded-full mb-8">
                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-300">
                                    AI Content Repurposing
                                </span>
                            </div>
                            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.95]">
                                Repurpose<br />
                                <span className="text-zinc-500">like a god.</span>
                            </h1>
                            <p className="mt-8 text-lg sm:text-xl text-zinc-400 max-w-xl leading-relaxed">
                                Paste a YouTube link or any text. We turn it into Instagram captions, TikTok hooks, LinkedIn threads, Shorts scripts &amp; newsletters — in seconds.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link to="/register">
                                    <Button data-testid="hero-cta" size="lg" className="bg-white text-black hover:bg-zinc-200 font-semibold rounded-md h-12 px-6 text-base">
                                        Start free — 5 repurposes/mo
                                        <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={2} />
                                    </Button>
                                </Link>
                                <Link to="/pricing">
                                    <Button data-testid="hero-pricing-btn" size="lg" variant="outline" className="bg-transparent border-white/20 hover:bg-white/5 text-white rounded-md h-12 px-6 text-base">
                                        See pricing
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-4 hidden lg:block">
                            <div className="border border-white/10 p-6 bg-black/40 backdrop-blur-md">
                                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">// LIVE STATS</div>
                                <div className="space-y-4">
                                    <Stat n="58s" l="avg. generation time" />
                                    <Stat n="5" l="platforms supported" />
                                    <Stat n="100+" l="repurposes / month on Pro" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Marquee of platforms */}
                <div className="relative mt-24 border-y border-white/10 overflow-hidden bg-black">
                    <div className="flex marquee gap-16 py-6 whitespace-nowrap">
                        {[...PLATFORMS, ...PLATFORMS, ...PLATFORMS].map((p, i) => (
                            <div key={`${p.name}-${i}`} className="flex items-center gap-3 text-zinc-500">
                                <p.icon className="w-5 h-5" strokeWidth={1.5} />
                                <span className="font-display font-bold tracking-[0.15em] text-sm">{p.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURES BENTO */}
            <section className="py-24 px-6 sm:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between mb-14">
                        <div>
                            <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">// 01 — WHY</div>
                            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight max-w-2xl">
                                One piece of content. <span className="text-zinc-500">Ten outputs.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-px bg-white/10 border border-white/10">
                        <FeatureCard
                            tag="01"
                            title="Paste anything"
                            body="A YouTube link or some text. We turn it into posts, threads and scripts."
                            icon={Zap}
                            className="md:col-span-1 row-span-1"
                        />
                        <FeatureCard
                            tag="02"
                            title="Five platforms, one click"
                            body="Instagram, TikTok, LinkedIn, Shorts scripts and a full newsletter — formatted natively."
                            icon={Sparkles}
                            className="md:col-span-2 row-span-1"
                            bg={FEATURE_BG}
                        />
                        <FeatureCard
                            tag="03"
                            title="Built for creators in a hurry"
                            body="From 1h video → a week of content in under a minute. Copy &amp; post."
                            icon={Clock}
                            className="md:col-span-2 row-span-1"
                        />
                        <FeatureCard
                            tag="04"
                            title="Your history, your archive"
                            body="Every repurpose is saved. Search, filter, recycle."
                            icon={Film}
                            className="md:col-span-1 row-span-1"
                        />
                    </div>
                </div>
            </section>

            {/* PRICING PREVIEW */}
            <section className="py-24 px-6 sm:px-10 border-t border-white/10">
                <div className="max-w-7xl mx-auto">
                    <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">// 02 — PRICING</div>
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
                            Three plans.<br /><span className="text-zinc-500">Pick yours.</span>
                        </h2>
                        <Link to="/pricing" data-testid="pricing-link-section">
                            <Button variant="outline" className="bg-transparent border-white/20 hover:bg-white/5 text-white rounded-md">
                                See full pricing <ArrowUpRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        <PricingMini name="Free" price="0€" features={["5 repurposes / month", "Instagram + TikTok", "History"]} />
                        <PricingMini name="Starter" price="9€" highlight features={["100 repurposes / month", "Instagram + TikTok", "Priority generation", "7-day free trial"]} />
                        <PricingMini name="Pro" price="29€" features={["500 repurposes / month", "All 5 platforms", "LinkedIn + Shorts + Newsletter", "7-day free trial"]} />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-6 sm:px-10 border-t border-white/10">
                <div className="max-w-5xl mx-auto text-center">
                    <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter">
                        Stop writing the same content<br />five times.
                    </h2>
                    <p className="mt-6 text-zinc-400 text-lg">It takes 30 seconds. Try it free.</p>
                    <div className="mt-10">
                        <Link to="/register">
                            <Button data-testid="footer-cta" size="lg" className="bg-white text-black hover:bg-zinc-200 font-semibold rounded-md h-12 px-8 text-base">
                                Get started — free
                                <ArrowUpRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/10 py-10 px-6 sm:px-10">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
                    <div className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-white text-black flex items-center justify-center font-display font-black text-[10px]">R</div>
                        <span className="font-display font-bold">RepurposeAI</span>
                        <span className="text-zinc-700">© 2026</span>
                    </div>
                    <div className="font-mono-ui text-xs tracking-widest uppercase">Built for creators in a rush.</div>
                </div>
            </footer>
        </div>
    );
}

function Stat({ n, l }) {
    return (
        <div>
            <div className="font-display font-black text-3xl tracking-tighter">{n}</div>
            <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{l}</div>
        </div>
    );
}

function FeatureCard({ tag, title, body, icon: Icon, className = "", bg }) {
    return (
        <div className={`relative p-8 bg-[#050505] hover:bg-[#080808] transition-colors min-h-[220px] flex flex-col justify-between overflow-hidden ${className}`}>
            {bg && (
                <div
                    className="absolute inset-0 opacity-20 bg-cover bg-center pointer-events-none"
                    style={{ backgroundImage: `url(${bg})` }}
                />
            )}
            <div className="relative flex items-start justify-between">
                <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-600">// {tag}</div>
                <Icon className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
            </div>
            <div className="relative">
                <div className="font-display font-bold text-2xl tracking-tight">{title}</div>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed max-w-md">{body}</p>
            </div>
        </div>
    );
}

function PricingMini({ name, price, features, highlight }) {
    return (
        <div className={`p-8 ${highlight ? "bg-white text-black" : "bg-[#050505]"}`}>
            <div className={`font-mono-ui text-[10px] tracking-[0.25em] uppercase ${highlight ? "text-black/60" : "text-zinc-500"}`}>{name}</div>
            <div className="mt-4 font-display font-black text-5xl tracking-tighter">{price}<span className={`text-base font-normal ${highlight ? "text-black/60" : "text-zinc-500"}`}>/mo</span></div>
            <ul className={`mt-6 space-y-2 text-sm ${highlight ? "text-black/80" : "text-zinc-400"}`}>
                {features.map((f) => <li key={f}>— {f}</li>)}
            </ul>
        </div>
    );
}
